import { type CatalogSource, parseCatalogStations, parseFacets } from '../../domain/catalog';
import { catalogApiUrl } from '../../domain/stream-url';
import type { CatalogPort, CatalogQuery, CatalogResult } from '../../ports/catalog';

/**
 * Odjemalec za `/api/catalog` — spletna baza postaj (T3 §1, T4 §1).
 *
 * Ime datoteke nosi primarni vir, vsebina pa ne ve, kdo je odgovoril: kateri
 * vir je bil uporabljen, pove Worker v polju `source`. Rezerva (WebRadioDB)
 * se zgodi tam in ne tu — sicer bi jo moral poznati vsak odjemalec posebej.
 */

/**
 * Naslov vira za **javljanje klika in nič drugega** (glej `reportClick`).
 *
 * Iskanje gre skozi Worker (`catalogApiUrl`); ta konstanta obstaja zato, ker
 * mora biti pri kliku vir naslova IP uporabnik in ne naš rob.
 */
const RADIO_BROWSER_BASE = 'https://all.api.radio-browser.info';

const SEARCH_TIMEOUT_MS = 10_000;
/** Izbire za combobox se ne spreminjajo med sejo; sme trajati dlje. */
const FACETS_TIMEOUT_MS = 12_000;
/** Ena stran zadetkov. Več od tega je na telefonu drsenje brez konca. */
export const CATALOG_PAGE_SIZE = 30;

interface SearchResponse {
  stations?: unknown;
  source?: unknown;
}

interface FacetsResponse {
  facets?: unknown;
}

function isCatalogSource(value: unknown): value is CatalogSource {
  return value === 'radio-browser' || value === 'webradiodb';
}

/**
 * Signal, ki odpade ob prekinitvi klicatelja **ali** po izteku časa.
 *
 * `AbortSignal.any()` je od 2024 v vseh ciljnih brskalnikih (R3); rezerva
 * obstaja zato, da starejši WebView ne ostane brez omejitve časa.
 */
function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const timeout = AbortSignal.timeout(ms);
  if (signal === undefined) return timeout;
  return typeof AbortSignal.any === 'function' ? AbortSignal.any([signal, timeout]) : signal;
}

export function createCatalogClient(base?: string): CatalogPort {
  return {
    async search(query: CatalogQuery, signal?: AbortSignal): Promise<CatalogResult> {
      const limit = query.limit ?? CATALOG_PAGE_SIZE;
      const params: Record<string, string> = { limit: String(limit) };

      // Prazen parameter ni isto kot odsoten: vir bi ga razumel kot iskanje
      // praznega niza in filter bi tiho zožil zadetke na nič.
      if (query.q.trim() !== '') params.q = query.q.trim();
      if (query.countryCode !== null) params.country = query.countryCode;
      if (query.tag !== null) params.tag = query.tag;
      if (query.offset !== undefined && query.offset > 0) params.offset = String(query.offset);

      let response: Response;
      try {
        response = await fetch(catalogApiUrl(params, base), {
          headers: { accept: 'application/json' },
          signal: withTimeout(signal, SEARCH_TIMEOUT_MS),
        });
      } catch {
        // Prekinitev klicatelja ni izpad: uporabnik je pretipkal poizvedbo in
        // zadetkov te ne rabi več. Vrne se kot neuspeh, a zaslon ga zavrže
        // (glej `store/search.ts`), sicer bi vsaka tipka pobliskala napako.
        if (signal?.aborted === true) return { ok: false, reason: 'unavailable' };
        return { ok: false, reason: navigator.onLine ? 'unavailable' : 'offline' };
      }

      if (!response.ok) return { ok: false, reason: 'unavailable' };

      const body = (await response.json()) as SearchResponse;
      const source: CatalogSource = isCatalogSource(body.source) ? body.source : 'radio-browser';
      const stations = parseCatalogStations(body.stations, source);

      return {
        ok: true,
        stations,
        source,
        // Manj od zahtevanega pomeni, da naslednje strani ni. Enako velja, kadar
        // je nekaj zadetkov odpadlo pri branju — takrat je stran vseeno zadnja.
        exhausted: !Array.isArray(body.stations) || body.stations.length < limit,
      };
    },

    /**
     * Izbire za combobox. Worker jih normalizira v `{ value, count }` — oba
     * vira ju imenujeta drugače (`name` proti `countrycode`) in preslikava
     * spada tja, kjer je vir znan.
     */
    async facets(kind, signal) {
      try {
        const response = await fetch(catalogApiUrl({ facet: kind }, base), {
          headers: { accept: 'application/json' },
          signal: withTimeout(signal, FACETS_TIMEOUT_MS),
        });
        if (!response.ok) return [];

        const body = (await response.json()) as FacetsResponse;
        return parseFacets(body.facets, 'value');
      } catch {
        // Filtri so izboljšava, ne pogoj: brez njih se še vedno išče po imenu.
        // Prazen seznam zaslon prebere kot »combobox ni na voljo« in ga skrije.
        return [];
      }
    },

    /**
     * Klik nazaj v radio-browser (P2 §2.8) — poštena vrnitev usluge.
     *
     * 🔴 **Ta klic gre mimo Workerja, in to je edina pravilna pot.**
     *
     * Vir šteje **en klik na naslov IP na postajo na dan** (dokumentirano,
     * preverjeno 2026-07-20). Iz Workerja bi bili vsi uporabniki en sam
     * naslov IP — deljeni izhodni naslov Cloudflara — in skupnost bi od cele
     * aplikacije dobila **en klik na postajo na dan**. Prispevek, ki je smisel
     * te funkcije, bi bil s tem izničen, ne pa zgolj zmanjšan.
     *
     * Isti razlog in isti precedens kot pri iTunes v fazi 3 (T3 §2): kadar je
     * pomemben **uporabnikov** naslov IP, poizveduje brskalnik. CSP to dopušča
     * (`connect-src https:`), CORS pri viru je `*`.
     *
     * `keepalive` in brez `await`: klic sme preživeti menjavo zaslona, ne sme
     * pa stati pred zvokom. Neuspeh ni viden nikjer in ne sme biti —
     * uporabniku »javljanje klika ni uspelo« ne pomeni ničesar.
     */
    reportClick(uuid) {
      // Zadetki iz rezervnega vira klikov ne poznajo; njihov `uuid` je naš
      // ključ s predpono in bi ga vir vrnil kot 404.
      if (!/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(uuid)) return;

      void fetch(`${RADIO_BROWSER_BASE}/json/url/${uuid}`, {
        keepalive: true,
        headers: { accept: 'application/json' },
      }).catch(() => {});
    },
  };
}
